import { FieldValues, SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  SerialisedPersonalListingProfileParams,
  usePersonalListingProfileProps,
} from "./page-props"
import { personalListingProfileSchema } from "./validation-schema"

import { FormRenderer } from "@/lib/data-driven-forms/renderer"
import { useMemo } from "react"
import { UPLOAD_FILE_TYPE } from "@/constants/ui"
import { useTObject, useTOptions } from "@/i18n"
import type { DataDrivenFormSchema } from "@/lib/data-driven-forms/schema"
import { typedEntries } from "@/types/helpers"
import { TagHierarchy } from "@/types/tag-hierarchy"
import { ParseKeys } from "i18next"

export default function Form({
  onSubmit,
  formId,
  titleId,
  registerDirtyDraft = false,
}: {
  onSubmit: (data: SerialisedPersonalListingProfileParams) => void
  formId: string
  titleId: string
  registerDirtyDraft?: boolean
}) {
  const { listing_profile, teacher_profile, teacher_profile_attachments } =
    usePersonalListingProfileProps()
  const defaultValues = {
    listing_profile,
    teacher_profile,
    teacher_profile_attachments,
  }
  const submitHandler: SubmitHandler<FieldValues> = async (data) => {
    onSubmit({
      listing_profile_json: JSON.stringify(data.listing_profile),
      teacher_profile_attachments: data.teacher_profile_attachments,
      teacher_profile_json: JSON.stringify(data.teacher_profile),
    })
  }
  const tagCategories = useTObject(
    "tag_categories",
    "listing_profile_tags",
  ) as unknown as TagHierarchy

  const formSchema = useMemo(() => {
    const schema: DataDrivenFormSchema<
      ["listing_profiles", "listing_profile_tags"]
    > = {
      type: "form",
      validator: personalListingProfileSchema,
      namespace: ["listing_profiles", "listing_profile_tags"],
      properties: [
        {
          type: "section:hash",
          sectionKey: "listing_details",
          title: "form.sections.listing_details",
          properties: [
            {
              type: "field:input",
              inputType: "text",
              name: "listing_profile.name",
              label: "form.listing_details_section.name",
            },
            {
              type: "field:input",
              inputType: "url",
              name: "listing_profile.webpage_url",
              label: "form.listing_details_section.webpage_url",
            },
            {
              type: "field:input",
              inputType: "url",
              name: "listing_profile.video_url",
              label: "form.listing_details_section.video_url",
            },
            {
              type: "field:textarea",
              name: "listing_profile.written_comment",
              label: "form.listing_details_section.written_comment",
            },
          ],
        },
        {
          type: "section:hash",
          sectionKey: "listing_tags",
          title: "form.sections.listing_tags",
          infoContentKey: "form.listing_tags_section.description",
          properties: typedEntries(tagCategories).map(
            ([tagCategoryKey, category]) => ({
              type: "section:scrolling",
              sectionKey: tagCategoryKey,
              title:
                `listing_profile_tags:tag_categories.${tagCategoryKey}.name` as ParseKeys<
                  ["listing_profiles", "listing_profile_tags"]
                >,
              infoContentKey:
                `listing_profile_tags:tag_categories.${tagCategoryKey}.description` as ParseKeys<
                  ["listing_profiles", "listing_profile_tags"]
                >,
              properties: typedEntries(category.tag_subcategories).flatMap(
                ([tagSubCategoryKey, subcategory]) =>
                  typedEntries(subcategory.tags).map(([tagKey, _tag]) => ({
                    type: "field:custom:toggle",
                    name: "listing_profile.tag_ids",
                    label:
                      `listing_profile_tags:tag_categories.${tagCategoryKey}.tag_subcategories.${tagSubCategoryKey}.tags.${tagKey}.name` as ParseKeys<
                        ["listing_profiles", "listing_profile_tags"]
                      >,
                    value: tagKey,
                    isArray: true,
                  })),
              ),
            }),
          ),
        },
        {
          type: "section:hash",
          sectionKey: "general_information",
          title: "form.sections.general_information",
          properties: [
            {
              type: "section:scrolling",
              sectionKey: "personal_information",
              title:
                "form.general_information_section.subsections.personal_information",
              properties: [
                {
                  type: "field:attachment",
                  accept: UPLOAD_FILE_TYPE.IMAGE,
                  useAttachment: () =>
                    usePersonalListingProfileProps().teacher_profile_attachments
                      .teacher_profile_photo_id,
                  label:
                    "form.general_information_section.personal_information.photo_id",
                  name: "teacher_profile_attachments.teacher_profile_photo_id.attachment",
                },
                {
                  type: "field:input",
                  inputType: "text",
                  label:
                    "form.general_information_section.personal_information.first_name",
                  name: "teacher_profile.first_name",
                },
                {
                  type: "field:input",
                  inputType: "text",
                  label:
                    "form.general_information_section.personal_information.last_name",
                  name: "teacher_profile.last_name",
                },
                {
                  type: "field:input",
                  inputType: "date",
                  label:
                    "form.general_information_section.personal_information.date_of_birth",
                  name: "teacher_profile.dob",
                },
                {
                  type: "field:combobox",
                  comboboxType: "single",
                  useOptions: () => useTOptions("user.gender", "common"),
                  name: "teacher_profile.gender_key",
                  label:
                    "form.general_information_section.personal_information.gender",
                },
                {
                  type: "field:combobox",
                  comboboxType: "single",
                  useOptions: () => useTOptions("religions", "religions"),
                  name: "teacher_profile.religion_key",
                  label:
                    "form.general_information_section.personal_information.religion",
                  tooltipContent:
                    "form.general_information_section.personal_information.tooltips.religion",
                  tooltipLink:
                    "https://www.abs.gov.au/statistics/classifications/australian-standard-classification-religious-groups/latest-release",
                },
                {
                  type: "field:combobox",
                  comboboxType: "single",
                  name: "teacher_profile.race_key",
                  label:
                    "form.general_information_section.personal_information.race",
                  useOptions: () =>
                    useTOptions("cultural_ethnic_groups", "races"),
                  tooltipContent:
                    "form.general_information_section.personal_information.tooltips.race",
                  tooltipLink:
                    "https://www.abs.gov.au/statistics/classifications/australian-standard-classification-cultural-and-ethnic-groups-ascceg/2019",
                },
                {
                  type: "field:combobox",
                  comboboxType: "single",
                  useOptions: () => useTOptions("languages", "languages"),
                  name: "teacher_profile.primary_language_key",
                  label:
                    "form.general_information_section.personal_information.primary_language",
                  tooltipContent:
                    "form.general_information_section.personal_information.tooltips.language",
                  tooltipLink:
                    "https://www.abs.gov.au/statistics/classifications/australian-standard-classification-languages-ascl/2025",
                },
                {
                  type: "field:combobox",
                  comboboxType: "single",
                  useOptions: () => useTOptions("languages", "languages"),
                  name: "teacher_profile.secondary_language_key",
                  label:
                    "form.general_information_section.personal_information.secondary_language",
                  tooltipContent:
                    "form.general_information_section.personal_information.tooltips.language",
                  tooltipLink:
                    "https://www.abs.gov.au/statistics/classifications/australian-standard-classification-languages-ascl/2025",
                },
                {
                  type: "field:combobox",
                  comboboxType: "multi",
                  useOptions: () => useTOptions("by_sacc_code", "countries"),
                  name: "teacher_profile.citizenship_country_sacc_codes",
                  label:
                    "form.general_information_section.personal_information.citizenship",
                  tooltipContent:
                    "form.general_information_section.personal_information.tooltips.country",
                  tooltipLink:
                    "https://www.abs.gov.au/statistics/classifications/standard-australian-classification-countries-sacc/2016",
                },
                {
                  type: "field:input",
                  inputType: "tel",
                  name: "teacher_profile.mobile",
                  label:
                    "form.general_information_section.personal_information.mobile_number",
                },
                {
                  type: "field:combobox",
                  comboboxType: "single",
                  useOptions: () => useTOptions("by_sacc_code", "countries"),
                  name: "teacher_profile.origin_country_sacc_code",
                  label:
                    "form.general_information_section.personal_information.country_of_origin",
                  tooltipContent:
                    "form.general_information_section.personal_information.tooltips.country",
                  tooltipLink:
                    "https://www.abs.gov.au/statistics/classifications/standard-australian-classification-countries-sacc/2016",
                },
              ],
            },
            {
              type: "section:scrolling",
              sectionKey: "residential_address",
              title:
                "form.general_information_section.subsections.residential_address",
              properties: [
                {
                  type: "field:place",
                  name: "teacher_profile.residential_address_details",
                  label:
                    "form.general_information_section.subsections.residential_address",
                },
              ],
            },
            {
              type: "section:scrolling",
              sectionKey: "personal_background_video",
              title:
                "form.general_information_section.subsections.personal_background_video",
              properties: [
                {
                  type: "field:input",
                  inputType: "url",
                  label:
                    "form.general_information_section.personal_background_video.background_video_link",
                  name: "teacher_profile.background_video_url",
                },
              ],
            },
          ],
        },
        {
          type: "section:hash",
          sectionKey: "accreditation",
          title: "form.sections.accreditation",
          properties: [
            {
              type: "section:scrolling",
              sectionKey: "state_accreditation",
              title:
                "form.accreditation_section.subsections.state_accreditation",
              properties: [
                {
                  type: "field:input",
                  inputType: "text",
                  name: "teacher_profile.accreditation_attributes.vic_approval_rego_number",
                  label:
                    "form.accreditation_section.state_approvals.vic_approval",
                },
                {
                  type: "field:input",
                  inputType: "text",
                  name: "teacher_profile.accreditation_attributes.nsw_approval_rego_number",
                  label:
                    "form.accreditation_section.state_approvals.nsw_approval",
                },
                {
                  type: "field:input",
                  inputType: "text",
                  name: "teacher_profile.accreditation_attributes.qld_approval_rego_number",
                  label:
                    "form.accreditation_section.state_approvals.qld_approval",
                },
                {
                  type: "field:input",
                  inputType: "text",
                  name: "teacher_profile.accreditation_attributes.wa_approval_rego_number",
                  label:
                    "form.accreditation_section.state_approvals.wa_approval",
                },
                {
                  type: "field:input",
                  inputType: "text",
                  name: "teacher_profile.accreditation_attributes.sa_approval_rego_number",
                  label:
                    "form.accreditation_section.state_approvals.sa_approval",
                },
                {
                  type: "field:input",
                  inputType: "text",
                  name: "teacher_profile.accreditation_attributes.tas_approval_rego_number",
                  label:
                    "form.accreditation_section.state_approvals.tas_approval",
                },
                {
                  type: "field:input",
                  inputType: "text",
                  name: "teacher_profile.accreditation_attributes.nt_approval_rego_number",
                  label:
                    "form.accreditation_section.state_approvals.nt_approval",
                },
                {
                  type: "field:input",
                  inputType: "text",
                  name: "teacher_profile.accreditation_attributes.act_approval_rego_number",
                  label:
                    "form.accreditation_section.state_approvals.act_approval",
                },
              ],
            },
            {
              type: "section:scrolling",
              sectionKey: "other_accreditation",
              title:
                "form.accreditation_section.subsections.other_accreditation",
              properties: [
                {
                  type: "field:custom:array",
                  name: "teacher_profile.accreditation_attributes.other_approvals_attributes",
                  label: "form.accreditation_section.other_accreditation.title",
                  defaultValue: { rego_number: "", description: "" },
                  addLabel:
                    "form.accreditation_section.other_accreditation.add_button",
                  removeLabel:
                    "form.accreditation_section.other_accreditation.remove_aria_label",
                  properties: [
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "rego_number",
                      label:
                        "form.accreditation_section.other_accreditation.registration_number",
                    },
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "description",
                      label:
                        "form.accreditation_section.other_accreditation.description_label",
                    },
                    {
                      type: "field:attachment",
                      name: "attachment",
                      accept: UPLOAD_FILE_TYPE.IMAGE_AND_PDF,
                      useAttachment: undefined,
                      label:
                        "form.accreditation_section.other_accreditation.accreditation_file",
                    },
                  ],
                  withAttachment: {
                    name: "teacher_profile_attachments.other_approval_photo_ids",
                    defaultValue: { attached: false, signed_id: null },
                  },
                },
              ],
            },
          ],
        },
        {
          type: "section:hash",
          sectionKey: "teaching_experience",
          title: "form.sections.teaching_experience",
          properties: [
            {
              type: "section:scrolling",
              sectionKey: "qualifications",
              title:
                "form.teaching_experience_section.subsections.qualifications",
              properties: [
                {
                  type: "field:custom:array",
                  name: "teacher_profile.experience_attributes.education_qualifications_attributes",
                  defaultValue: {
                    qualification: "",
                    university_name: "",
                    year_obtained: "",
                  },
                  label:
                    "form.teaching_experience_section.qualifications.title",
                  addLabel:
                    "form.teaching_experience_section.qualifications.add_button",
                  removeLabel:
                    "form.teaching_experience_section.qualifications.remove_aria_label",
                  properties: [
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "qualification",
                      label:
                        "form.teaching_experience_section.qualifications.qualification",
                    },
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "university_name",
                      label:
                        "form.teaching_experience_section.qualifications.university_name",
                    },
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "year_obtained",
                      label:
                        "form.teaching_experience_section.qualifications.year_obtained",
                    },
                  ],
                },
              ],
            },
            {
              type: "section:scrolling",
              sectionKey: "subject_experience",
              title:
                "form.teaching_experience_section.subsections.subject_experience",
              properties: [
                {
                  type: "field:custom:array",
                  name: "teacher_profile.experience_attributes.teaching_experiences_attributes",
                  defaultValue: {
                    subject: "",
                    year_level: "",
                    year: { from: "", to: "" },
                  },
                  label:
                    "form.teaching_experience_section.subject_experience.title",
                  addLabel:
                    "form.teaching_experience_section.subject_experience.add_button",
                  removeLabel:
                    "form.teaching_experience_section.subject_experience.remove_aria_label",
                  properties: [
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "subject",
                      label:
                        "form.teaching_experience_section.subject_experience.subject",
                    },
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "year_level",
                      label:
                        "form.teaching_experience_section.subject_experience.year_level",
                    },
                    {
                      type: "field:custom:value-range",
                      name: "year",
                      properties: [
                        {
                          type: "field:input",
                          inputType: "text",
                          name: "from",
                          label:
                            "form.teaching_experience_section.subject_experience.from_year",
                        },
                        {
                          type: "field:input",
                          inputType: "text",
                          name: "to",
                          label:
                            "form.teaching_experience_section.subject_experience.to_year",
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: "section:scrolling",
              sectionKey: "work_experience",
              title:
                "form.teaching_experience_section.subsections.work_experience",
              properties: [
                {
                  type: "field:custom:array",
                  name: "teacher_profile.experience_attributes.work_experiences_attributes",
                  defaultValue: {
                    company_name: "",
                    title: "",
                    year: { from: "", to: "" },
                  },
                  label:
                    "form.teaching_experience_section.work_experience.title",
                  addLabel:
                    "form.teaching_experience_section.work_experience.add_button",
                  removeLabel:
                    "form.teaching_experience_section.work_experience.remove_aria_label",
                  properties: [
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "company_name",
                      label:
                        "form.teaching_experience_section.work_experience.company_name",
                    },
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "title",
                      label:
                        "form.teaching_experience_section.work_experience.role",
                    },
                    {
                      type: "field:custom:value-range",
                      name: "year",
                      properties: [
                        {
                          type: "field:input",
                          inputType: "text",
                          name: "from",
                          label:
                            "form.teaching_experience_section.work_experience.from_year",
                        },
                        {
                          type: "field:input",
                          inputType: "text",
                          name: "to",
                          label:
                            "form.teaching_experience_section.work_experience.to_year",
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: "section:scrolling",
              sectionKey: "memberships",
              title: "form.teaching_experience_section.subsections.memberships",
              properties: [
                {
                  type: "field:custom:array",
                  name: "teacher_profile.experience_attributes.memberships_attributes",
                  defaultValue: { membership: "", link: "" },
                  label: "form.teaching_experience_section.memberships.title",
                  addLabel:
                    "form.teaching_experience_section.memberships.add_button",
                  removeLabel:
                    "form.teaching_experience_section.memberships.remove_aria_label",
                  properties: [
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "membership",
                      label:
                        "form.teaching_experience_section.memberships.membership",
                    },
                    {
                      type: "field:input",
                      inputType: "text",
                      name: "link",
                      label:
                        "form.teaching_experience_section.memberships.link",
                    },
                  ],
                },
              ],
            },
            {
              type: "section:scrolling",
              sectionKey: "types_of_students_taught",
              title:
                "form.teaching_experience_section.subsections.types_of_students_taught",
              properties: [
                {
                  type: "field:combobox",
                  comboboxType: "multi",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.gender_keys",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.gender",
                  useOptions: () => useTOptions("user.gender", "common"),
                },
                {
                  type: "field:textarea",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.gender_notes",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.gender_notes",
                },
                {
                  type: "field:combobox",
                  comboboxType: "multi",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.religion_keys",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.religion",
                  useOptions: () => useTOptions("religions", "religions"),
                },
                {
                  type: "field:textarea",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.religion_note",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.religion_notes",
                },
                {
                  type: "field:combobox",
                  comboboxType: "multi",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.primary_language_keys",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.primary_language",
                  useOptions: () => useTOptions("languages", "languages"),
                },
                {
                  type: "field:textarea",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.primary_language_notes",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.primary_language_notes",
                },
                {
                  type: "field:combobox",
                  comboboxType: "multi",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.secondary_language_keys",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.secondary_language",
                  useOptions: () => useTOptions("languages", "languages"),
                },
                {
                  type: "field:textarea",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.secondary_language_notes",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.secondary_language_notes",
                },
                {
                  type: "field:combobox",
                  comboboxType: "multi",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.eyesights",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.eyesight",
                  useOptions: () =>
                    useTOptions(
                      "visual_impairment_options",
                      "student_attributes_options",
                    ),
                },
                {
                  type: "field:textarea",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.eyesight_notes",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.eyesight_notes",
                },
                {
                  type: "field:combobox",
                  comboboxType: "multi",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.adhds",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.adhd",
                  useOptions: () =>
                    useTOptions("adhd_options", "student_attributes_options"),
                },
                {
                  type: "field:textarea",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.adhd_notes",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.adhd_notes",
                },
                {
                  type: "field:combobox",
                  comboboxType: "multi",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.depressions",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.depression",
                  useOptions: () =>
                    useTOptions(
                      "depression_options",
                      "student_attributes_options",
                    ),
                },
                {
                  type: "field:textarea",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.depression_notes",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.depression_notes",
                },
                {
                  type: "field:textarea",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.intellectual_ability_notes",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.intellectual_ability_notes",
                },
                {
                  type: "field:textarea",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.socioeconomic_notes",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.socioeconomic_notes",
                },
                {
                  type: "field:textarea",
                  name: "teacher_profile.experience_attributes.students_taught_attributes.other_notes",
                  label:
                    "form.teaching_experience_section.types_of_students_taught.other_notes",
                },
              ],
            },
            {
              type: "section:scrolling",
              sectionKey: "other_experience",
              title:
                "form.teaching_experience_section.subsections.other_experience",
              properties: [
                {
                  type: "field:custom:array",
                  name: "teacher_profile.experience_attributes.teacher_experience_assessor_subjects_attributes",
                  defaultValue: {
                    subject_id: null,
                    year: { from: "", to: "" },
                  },
                  label:
                    "form.teaching_experience_section.other_experience.assessor_title",
                  addLabel:
                    "form.teaching_experience_section.other_experience.add_assessor_button",
                  removeLabel:
                    "form.teaching_experience_section.other_experience.remove_assessor_aria_label",
                  properties: [
                    {
                      type: "field:custom:subject",
                      name: "subject_key",
                      label:
                        "form.teaching_experience_section.other_experience.subject",
                      curriculaLabel:
                        "form.teaching_experience_section.other_experience.state",
                    },
                    {
                      type: "field:custom:value-range",
                      name: "year",
                      properties: [
                        {
                          type: "field:input",
                          inputType: "text",
                          name: "from",
                          label:
                            "form.teaching_experience_section.other_experience.from_year",
                        },
                        {
                          type: "field:input",
                          inputType: "text",
                          name: "to",
                          label:
                            "form.teaching_experience_section.other_experience.to_year",
                        },
                      ],
                    },
                  ],
                },
                {
                  type: "field:custom:array",
                  name: "teacher_profile.experience_attributes.teacher_experience_examiner_subjects_attributes",
                  defaultValue: {
                    subject_id: null,
                    year: { from: "", to: "" },
                  },
                  label:
                    "form.teaching_experience_section.other_experience.examiner_title",
                  addLabel:
                    "form.teaching_experience_section.other_experience.add_examiner_button",
                  removeLabel:
                    "form.teaching_experience_section.other_experience.remove_examiner_aria_label",
                  properties: [
                    {
                      type: "field:custom:subject",
                      name: "subject_key",
                      label:
                        "form.teaching_experience_section.other_experience.subject",
                      curriculaLabel:
                        "form.teaching_experience_section.other_experience.state",
                    },
                    {
                      type: "field:custom:value-range",
                      name: "year",
                      properties: [
                        {
                          type: "field:input",
                          inputType: "text",
                          name: "from",
                          label:
                            "form.teaching_experience_section.other_experience.from_year",
                        },
                        {
                          type: "field:input",
                          inputType: "text",
                          name: "to",
                          label:
                            "form.teaching_experience_section.other_experience.to_year",
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              type: "section:scrolling",
              sectionKey: "resume",
              title: "form.teaching_experience_section.subsections.resume",
              properties: [
                {
                  type: "field:attachment",
                  accept: ".pdf,.jpg,.jpeg,.png",
                  useAttachment: () =>
                    usePersonalListingProfileProps().teacher_profile_attachments
                      .teacher_profile_resume,
                  label: "form.teaching_experience_section.resume.resume_file",
                  name: "teacher_profile_attachments.teacher_profile_resume.attachment",
                },
              ],
            },
          ],
        },
        {
          type: "section:hash",
          sectionKey: "self_assessment",
          title: "form.sections.self_assessment",
          properties: [
            {
              type: "field:radioTable",
              label: "form.self_assessment_section.title",
              name: "teacher_profile.teaching_style_attributes",
              useOptions: () => {
                const { t } = useTranslation("listing_profiles")

                return [
                  {
                    label: t("form.self_assessment_section.rating_scale.1"),
                    value: 1,
                  },
                  {
                    label: t("form.self_assessment_section.rating_scale.2"),
                    value: 2,
                  },
                  {
                    label: t("form.self_assessment_section.rating_scale.3"),
                    value: 3,
                  },
                  {
                    label: t("form.self_assessment_section.rating_scale.4"),
                    value: 4,
                  },
                  {
                    label: t("form.self_assessment_section.rating_scale.5"),
                    value: 5,
                  },
                ]
              },
              useRows: () => {
                const { t } = useTranslation("listing_profiles")

                return [
                  {
                    value:
                      "teacher_profile.teaching_style_attributes.style_autocratic",
                    label: t(
                      "form.self_assessment_section.teaching_styles.autocratic",
                    ),
                  },
                  {
                    value:
                      "teacher_profile.teaching_style_attributes.style_reserved",
                    label: t(
                      "form.self_assessment_section.teaching_styles.reserved",
                    ),
                  },
                  {
                    value:
                      "teacher_profile.teaching_style_attributes.style_talk",
                    label: t(
                      "form.self_assessment_section.teaching_styles.talk",
                    ),
                  },
                  {
                    value:
                      "teacher_profile.teaching_style_attributes.style_consultative",
                    label: t(
                      "form.self_assessment_section.teaching_styles.consultative",
                    ),
                  },
                  {
                    value:
                      "teacher_profile.teaching_style_attributes.style_participative",
                    label: t(
                      "form.self_assessment_section.teaching_styles.participative",
                    ),
                  },
                ]
              },
            },
            {
              type: "field:textarea",
              name: "teacher_profile.teaching_style_attributes.style_comment",
              label: "form.self_assessment_section.teaching_style_comment",
            },
          ],
        },
      ],
    }
    return schema
  }, [tagCategories])

  return (
    <FormRenderer
      schema={formSchema}
      formId={formId}
      onSubmit={submitHandler}
      defaultValues={defaultValues}
      titleId={titleId}
      registerDirtyDraft={registerDirtyDraft}
    />
  )
}
